import React, { useState } from 'react';
import { ThrottledBucket } from './useRateLimits';

// AIDEV-NOTE: Validated against the dataviz CVD checks, which TCI purple and teal-blue fail.
// Re-validate before changing.
const VENDOR_COLORS: Record<string, string> = {
  canvas: '#2b9ab8',
  schoology: '#d14801',
};

const VENDORS = ['canvas', 'schoology'];

// Roughly five labels whatever the bucket count.
const AXIS_LABEL_COUNT = 5;

const label = (at: string, bucketIsHour: boolean) => {
  const date = new Date(at);

  return bucketIsHour
    ? date.toLocaleTimeString([], { hour: 'numeric' })
    : date.toLocaleDateString([], { day: 'numeric', month: 'short' });
};

interface ThrottledOverTimeProps {
  bucketIsHour: boolean;
  rows: ThrottledBucket[];
}

// Stacked bars rather than lines: counts are frequently zero, which reads as an absent bar but as a
// misleading dip on a line.
const ThrottledOverTime = ({ bucketIsHour, rows }: ThrottledOverTimeProps) => {
  const [hovered, setHovered] = useState<string | null>(null);
  const buckets = Array.from(new Set(rows.map(row => row.at))).sort();

  if (buckets.length === 0) {
    return (
      <p className="tw-mb-6 tw-mt-0 tw-text-sm tw-text-spa-secondary">
        No rate limiting in this window.
      </p>
    );
  }

  const countFor = (at: string, vendor: string) => (
    rows.find(row => row.at === at && row.vendor === vendor)?.count ?? 0
  );
  const totalFor = (at: string) => VENDORS.reduce((sum, vendor) => sum + countFor(at, vendor), 0);
  const peak = Math.max(...buckets.map(totalFor));
  const describe = (at: string) => `${label(at, bucketIsHour)} · ${VENDORS
    .map(vendor => `${vendor} ${countFor(at, vendor).toLocaleString()}`)
    .join(', ')}`;

  const readout = hovered ? describe(hovered) : `peak ${peak.toLocaleString()}`;

  const axisStep = Math.max(Math.ceil(buckets.length / AXIS_LABEL_COUNT), 1);

  return (
    <>
      {/* Legend first: with two series, identity must never rest on colour alone. */}
      <div className="tw-mb-2 tw-flex tw-items-center tw-gap-4">
        {VENDORS.map(vendor => (
          <span key={vendor} className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-text-spa-secondary">
            <span
              className="tw-inline-block tw-h-2.5 tw-w-2.5 tw-rounded-sm"
              style={{ backgroundColor: VENDOR_COLORS[vendor] }}
            />
            {vendor}
          </span>
        ))}
        <span className="tw-ml-auto tw-tabular-nums tw-text-sm tw-text-spa-tertiary">{readout}</span>
      </div>

      <div className="tw-mb-1 tw-flex tw-h-24 tw-items-end tw-gap-0.5" onMouseLeave={() => setHovered(null)}>
        {buckets.map((at) => {
          const total = totalFor(at);

          return (
            <div
              key={at}
              aria-label={describe(at)}
              className="tw-flex tw-h-full tw-flex-1 tw-flex-col tw-justify-end tw-opacity-100
                hover:tw-opacity-75"
              role="img"
              onMouseEnter={() => setHovered(at)}
            >
              {VENDORS.map((vendor) => {
                const count = countFor(at, vendor);
                if (count === 0) return null;

                return (
                  <div
                    key={vendor}
                    // 2px surface gap between stacked segments so they read as separate marks.
                    className="tw-rounded-sm"
                    style={{
                      backgroundColor: VENDOR_COLORS[vendor],
                      height: `${Math.max((count / peak) * 100, 2)}%`,
                      marginTop: 2,
                    }}
                  />
                );
              })}
              {total === 0 && <div className="tw-h-0.5 tw-rounded-sm tw-bg-spa-border" />}
            </div>
          );
        })}
      </div>

      {/* Evenly spaced rather than every bucket — a timestamp under all 24 is noise. */}
      <div className="tw-mb-6 tw-flex tw-text-xs tw-text-spa-secondary">
        {buckets.map((at, index) => (
          <span key={at} className="tw-flex-1 tw-text-center">
            {index % axisStep === 0 ? label(at, bucketIsHour) : ' '}
          </span>
        ))}
      </div>
    </>
  );
};

export default ThrottledOverTime;
